import { useEffect, useState } from 'react';
import { useApi } from './useApi';
import API_ENDPOINTS from '../constants/apiEndpoints';

export const UNREAD_POLL_MS = 30000;

/**
 * Unread customer-message count for the sidebar badge.
 *
 * Fetches on mount, then polls every UNREAD_POLL_MS while the tab is visible,
 * and refetches immediately on tab refocus (visibilitychange/window focus) so
 * a returning admin never sees a stale badge. An in-flight flag keeps a slow
 * backend from stacking overlapping requests. Silently keeps the last value
 * on any error so the sidebar never breaks on a failed fetch.
 */
export const useUnreadMessagesCount = enabled => {
  const api = useApi();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let inFlight = false;
    const fetchCount = () => {
      if (inFlight) return;
      inFlight = true;
      api
        .get(API_ENDPOINTS.MESSAGES.UNREAD_COUNT)
        .then(data => {
          if (!cancelled) setCount(data?.unread || 0);
        })
        .catch(() => {
          // Badge is best-effort — never surface an error for it.
        })
        .finally(() => {
          inFlight = false;
        });
    };
    fetchCount();
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      fetchCount();
    }, UNREAD_POLL_MS);
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      fetchCount();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return count;
};
